'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'

import { usePDP } from '@src/hooks/usePDP'
import { useLocale } from '@src/i18n/LocaleProvider'
import { formatCartAmount } from '@src/utils/cart.util'

export const ProductJsonLdSchema = () => {
  const { viewModel } = usePDP()
  const { locale, t } = useLocale()
  const pathname = usePathname()

  const productSchema = useMemo(() => {
    const description = viewModel?.product?.productShortDescription?.[locale.code]
    // sanitize and remove html tags
    const descriptionText = sanitizeHtml(description ?? '', {
      allowedTags: [],
      allowedAttributes: {},
    })

    const variantsSchemas = viewModel?.variants?.map((variant) => {
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        brand: {
          '@type': 'Brand',
          name: 'BODi',
        },
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          url: `https://www.bodi.com${pathname}`,
          priceCurrency: variant?.prices?.[0]?.value?.currencyCode,
          price: formatCartAmount(variant?.prices?.[0]?.value?.centAmount ?? 0, t),
        },
        description: descriptionText,
        name: variant?.attributes?.find((attribute) => attribute?.name === 'displayName')?.value?.[
          locale.code
        ],
        image: variant?.assets?.[0]?.sources?.[0]?.uri,
        sku: variant?.sku,
      }

      return schema
    })
    return variantsSchemas
  }, [locale.code, t, viewModel?.product?.productShortDescription, viewModel?.variants, pathname])

  return (
    <span key="productJsonLdSchema">
      {productSchema?.map((schema) => (
        // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
        <Script
          key={schema.sku}
          strategy="beforeInteractive"
          id={`product-schema-${schema.sku}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </span>
  )
}
